import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators, compose } from "redux";
import moment from "moment";
import _ from "lodash";
import { authorizedRequest } from "utils/api";
import { ButtonSizeOptions, ButtonText, SourceFileLoader } from "@loanbossinc/component-library";

import SimpleConfirmDialog from "../../../../components/Global/ConfirmationDialogs/SimpleConfirmDialog";
import SimpleInfoDialog from "components/Global/ConfirmationDialogs/SimpleInfoDialog";

import { actionCreators } from "./sourceFileVersions.store";
import {
  ColumnWrapper,
  CreateDateWrapper,
  LeftDetailWrapper,
  PaddedWrapperFullWidth,
  RightDetailWrapper,
  Row,
  StyledPrimaryButton,
  Title,
  VersionUploadWrapper
} from "./styles";
import SourceFileVersionListItem from "./sourceFileVersionListItem";
import SourceFilePreview from "./SourceFilePreview";

const SUPPORTED_FILE_TYPES = [".csv", ".xls", ".xlsx"];

const SourceFileVersions = props => {
  const {
    backToList,
    dealId,
    getSourceFileVersions,
    isConfirmDialogOpen,
    isOverriding,
    isUploading,
    selectedSourceFile,
    selectedSourceFileVersions,
    allVersionsForDeal,
    setSourceFileVersionsSelections,
    selectedVersion,
    uploadErrorMessage,
    uploadSourceFileVersion,
    uploadSuccessful,
    isPreview,
    isOpen,
    largeMriFileModal,
    companyId,
    setSourceFilesToDelete,
    getAllVersions,
    getSourceFilesByDeal,
    user: { id: userId }
  } = props;

  const [latestSourceFileVersion, setLatestSourceFileVersion] = useState(null);
  const [firstSourceFileVersion, setFirstSourceFileVersion] = useState(null);
  const [parentAndVersionsFiles, setParentAndVersionsFiles] = useState([]);
  const rentRollFileTypes = ["rent-roll", "mf-rent-roll", "mf-rent-roll-charge-code"];

  useEffect(() => {
    if (selectedSourceFile && selectedSourceFileVersions && allVersionsForDeal) {
      let sortedParentAndVersionsFiles = [];

      if (rentRollFileTypes.includes(selectedSourceFile.typeId)){
        sortedParentAndVersionsFiles = _.orderBy(
          allVersionsForDeal.filter((v) => (rentRollFileTypes.includes(v.typeId)) && v.propertyId === selectedSourceFile.propertyId && v.sourceName === selectedSourceFile.sourceName ),
          (v) => moment(v.createdAt),
          "desc"
        );
      } else {
        sortedParentAndVersionsFiles = _.orderBy([selectedSourceFile, ...selectedSourceFileVersions], v => moment(v.createdAt), "desc");
      }

      setLatestSourceFileVersion(sortedParentAndVersionsFiles[0]);
      setFirstSourceFileVersion(sortedParentAndVersionsFiles[sortedParentAndVersionsFiles.length - 1]);
      setParentAndVersionsFiles(sortedParentAndVersionsFiles);
      const versionIds = [];
      sortedParentAndVersionsFiles.forEach(file => {
        versionIds.push(file.id);
      });
      setSourceFilesToDelete(versionIds);
    }
  }, [selectedSourceFile, selectedSourceFileVersions, allVersionsForDeal]);

  useEffect(() => {
    if (uploadSuccessful) {
      getSourceFileVersions({ dealId, selectedSourceFile });
      getAllVersions({ dealId });
      setSourceFileVersionsSelections({ isOverriding: false, uploadSuccessful: false, uploadErrorMessage: null });
    }
  }, [uploadSuccessful]);

  useEffect(() => {
    if (!isOpen) {
      setSourceFileVersionsSelections({ isOverriding: false, isPreview: false });
    }
  }, [isOpen]);

  const downloadFile = async sourceFile => {
    const response = await authorizedRequest(`/source-files/download/${sourceFile.id}`, "GET", null, null, true);

    const link = document.createElement("a");
    link.href = response.url;
    link.setAttribute("download", `${sourceFile.originalName}`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const previewFile = () => (isPreview && !isOverriding && latestSourceFileVersion ? <SourceFilePreview previewFile={latestSourceFileVersion} isPreview /> : null);

  const onDeleteSourceFile = sourceFile => {
    setSourceFileVersionsSelections({ isConfirmDialogOpen: true, selectedVersion: sourceFile });
  };

  const onConfirmDeleteSourceFile = async () => {
    if (selectedVersion.typeId === "rent-roll" || selectedVersion.typeId === "mf-rent-roll" || selectedVersion.typeId === "mf-rent-roll-charge-code") {
      const { id } = selectedVersion;
      await authorizedRequest(`/source-files/${id}`, "DELETE", null, null, true);
      await authorizedRequest(`/rent-rolls/deleteBySourceFileId/${dealId}/${id}`, "DELETE", null, null, true);
      if (parentAndVersionsFiles.length === 1) {
        getSourceFilesByDeal({ dealId });
        backToList();
      }
      getAllVersions({ dealId });
    } else {
      await authorizedRequest(`/source-files/${selectedVersion.id}`, "DELETE", null, null, true);
      getSourceFileVersions({ dealId, selectedSourceFile });
    }
    setSourceFileVersionsSelections({ isConfirmDialogOpen: false, selectedVersion: null });
  };

  const handleUploadError = errors => {
    const error = errors[0].errors[0];

    let message;
    switch (error.code) {
      case "file-too-large":
        message = "File size has to be 1 Mb or less";
        break;
      case "file-invalid-type":
        message = "File type must be one of csv, xls, xlsx";
        break;
      default:
        message = "Unsupported File";
    }

    setSourceFileVersionsSelections({ uploadErrorMessage: message });
  };

  const onOverride = () => {
    const loaderMessage = !uploadErrorMessage ? "Add a file by dragging it here, or" : uploadErrorMessage;
    const fileLoaderTextContent = !isUploading ? loaderMessage : "Uploading ...";

    return !isPreview && isOverriding ? (
      <VersionUploadWrapper>
        <SourceFileLoader
          content={fileLoaderTextContent}
          multiple={false}
          onDrop={(files, error) => {
            if (error.length) {
              handleUploadError(error);
            } else {
              uploadSourceFileVersion(selectedSourceFile, userId, files[0], companyId);
            }
          }}
          accept={SUPPORTED_FILE_TYPES}
          maxSize={100000000}
        />
      </VersionUploadWrapper>
    ) : null;
  };

  let parentAndVersionsRows = [];

  if (selectedSourceFileVersions) {
    parentAndVersionsRows = _.orderBy(parentAndVersionsFiles, ["cratedAt"], ["desc"]).map(sf => (
      <SourceFileVersionListItem key={sf.id} sourceFile={sf} deleteSourceFile={onDeleteSourceFile} downloadFile={downloadFile} />
    ));
  }

  return (
    latestSourceFileVersion ? (
      <>
        <Row>
          <PaddedWrapperFullWidth padding="0">
            <LeftDetailWrapper>
              <ColumnWrapper>
                <div>
                  <ButtonText id="back-button" onClick={backToList} label="" reverse iconName="arrow_forward" />
                </div>
                <Title fontSize="20px" paddingTop="5px">
                  {latestSourceFileVersion.sourceName}
                  {" "}
                  {latestSourceFileVersion.typeName}
                </Title>
              </ColumnWrapper>
              <CreateDateWrapper>
                {`Created on ${moment.utc(firstSourceFileVersion.createdAt).format("MMMM Do, YYYY")}`}
              </CreateDateWrapper>
            </LeftDetailWrapper>
            <RightDetailWrapper>
              <StyledPrimaryButton
                size={ButtonSizeOptions.sm}
                label="Upload"
                onClick={() => {
                  setSourceFileVersionsSelections({ isPreview: false, isOverriding: !isOverriding });
                  onOverride();
                }}
              />
            </RightDetailWrapper>
          </PaddedWrapperFullWidth>
        </Row>

        {!isPreview && !isOverriding && parentAndVersionsRows}
        {onOverride()}
        {previewFile()}
        <SimpleConfirmDialog
          modalOpen={isConfirmDialogOpen}
          title="Confirm Deletion"
          message="Are you sure you want to delete this version?"
          yesText="Yes, Delete"
          noText="No"
          handleNo={() => setSourceFileVersionsSelections({ isConfirmDialogOpen: false })}
          handleYes={onConfirmDeleteSourceFile}
        />
        <SimpleInfoDialog
          title="Loading MRI file"
          message="File upload is processing. This may take several minutes."
          modalOpen={largeMriFileModal}
          handleClose={() => setSourceFileVersionsSelections({ largeMriFileModal: false })}
          handleAccept={() => setSourceFileVersionsSelections({ largeMriFileModal: false })}
        />
      </>
    ) : null
  );
};

const mapStateToProps = ({ lenderReportingSFVersions, authentication: { user }, lenderReportSourceFileManagement: { allVersionsForDeal } }) => ({
  ...lenderReportingSFVersions,
  user,
  allVersionsForDeal
});
const mapDispatchToProps = (dispatch) => bindActionCreators({ ...actionCreators }, dispatch);

export default compose(connect(mapStateToProps, mapDispatchToProps))(SourceFileVersions);
